import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    Form,
    Input,
    ScrollArea,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@repo/shadcn';
import { format } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TbTrash } from 'react-icons/tb';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { closeModal } from '../../../features/slice/event/event-slice';
import { useLazyGetSingleOrderQuery } from '../../../features/slice/order';
import { useCreateSalesReturnMutation } from '../../../features/slice/sales-return/sales-return.api-slice';
import { logError } from '../../../utils';
import { formatCurrencyInBDT } from '../../../utils/currency-formater';
import { SALES_RETURN_RATE } from './constants';

type SalesReturnOrderProps = {
    orderId?: string;
};

export const SalesReturnOrder: FC<SalesReturnOrderProps> = ({ orderId }) => {
    const [getSingleOrderById, { isLoading, isError, isSuccess, data }] =
        useLazyGetSingleOrderQuery();

    const dispatch = useDispatch();

    const [createSalesReturn] = useCreateSalesReturnMutation();

    const [returnItems, setReturnItems] = useState<
        Record<
            string,
            { id: string; quantity: number; price: number; rate: number }
        >
    >({});

    const form = useForm({});

    const onSubmit = async (e: any) => {
        e.preventDefault();
        try {
            const payload = {
                order_id: orderId,
                items: Object.values(returnItems).map((item) => ({
                    id: item.id,
                    quantity: item.quantity,
                    price: item.price,
                    rate: item.rate,
                })),
            };
            await createSalesReturn(payload).unwrap();
            toast.success('Sales return created successfully');
            dispatch(closeModal());
            setReturnItems({});
        } catch (error) {
            logError('SalesReturnOrder:', error);
            toast.error('Failed to create sales return');
        }
    };

    const handleChangeQuantity = (quantity: number, orderItemId: string) => {
        setReturnItems((prevState) => {
            if (prevState[orderItemId]) {
                return {
                    ...prevState,
                    [orderItemId]: {
                        ...prevState[orderItemId],
                        quantity: quantity,
                    },
                };
            }

            const orderItem = data?.data?.items?.find(
                (item) => item.id === orderItemId,
            );

            return {
                ...prevState,
                [orderItemId]: {
                    id: orderItemId,
                    price: orderItem?.price ?? 0,
                    quantity: quantity,
                    rate: SALES_RETURN_RATE,
                },
            };
        });
    };

    const handleChangeRate = (rate: number, orderItemId: string) => {
        setReturnItems((prevState) => {
            if (prevState[orderItemId]) {
                return {
                    ...prevState,
                    [orderItemId]: {
                        ...prevState[orderItemId],
                        rate: rate,
                    },
                };
            }

            const orderItem = data?.data?.items?.find(
                (item) => item.id === orderItemId,
            );

            return {
                ...prevState,
                [orderItemId]: {
                    id: orderItemId,
                    price: orderItem?.price ?? 0,
                    quantity: orderItem?.quantity ?? 0,
                    rate: rate,
                },
            };
        });
    };

    useEffect(() => {
        if (orderId) {
            getSingleOrderById(orderId);
        }
    }, [orderId]);

    if (!orderId) {
        return (
            <div className="flex items-center justify-center py-6 text-black/50">
                Select an Order
            </div>
        );
    }

    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (!isLoading && isError) {
        return <div>Something went wrong!</div>;
    }

    if (isSuccess) {
        return (
            <div>
                <Form {...form}>
                    <form onSubmit={onSubmit}>
                        <div className="py-4">
                            <h3>Order:</h3>
                            <div className="flex flex-col">
                                <span>
                                    Invoice No: {data?.data?.invoice_number}
                                </span>
                                <span>
                                    Date:{' '}
                                    {format(
                                        data?.data?.created_at ?? '',
                                        'dd-MMM-yyyy',
                                    )}
                                </span>
                                <span>
                                    Time:{' '}
                                    {format(data?.data?.created_at ?? '', 'p')}
                                </span>
                                {data?.data?.customer?.name && (
                                    <span>
                                        Customer: {data?.data?.customer?.name}
                                    </span>
                                )}
                            </div>
                        </div>
                        <ScrollArea className={``}>
                            <Table className="relative">
                                <TableHeader className="sticky top-0 z-10 h-[50px] bg-slate-50">
                                    <TableRow>
                                        <TableHead>Medicine</TableHead>
                                        <TableHead>Price</TableHead>
                                        <TableHead>Quantity</TableHead>
                                        <TableHead>Discount</TableHead>
                                        <TableHead>Total</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>

                                <TableBody>
                                    {data?.data?.items.map((orderItem) => (
                                        <TableRow key={orderItem.id}>
                                            <TableCell>
                                                {' '}
                                                {orderItem.product_name}
                                            </TableCell>
                                            <TableCell className="w-[100px]">
                                                {formatCurrencyInBDT(
                                                    orderItem.price,
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center space-x-2">
                                                    <Input
                                                        value={
                                                            returnItems[
                                                                orderItem.id
                                                            ]?.quantity ??
                                                            orderItem.quantity
                                                        }
                                                        type="number"
                                                        onChange={(e) =>
                                                            handleChangeQuantity(
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                                orderItem.id,
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center space-x-2">
                                                    <Input
                                                        value={
                                                            returnItems[
                                                                orderItem.id
                                                            ]?.rate ??
                                                            SALES_RETURN_RATE
                                                        }
                                                        type="number"
                                                        onChange={(e) =>
                                                            handleChangeRate(
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                                orderItem.id,
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell className="white-space-nowrap w-[100px] font-semibold">
                                                {formatCurrencyInBDT(
                                                    orderItem.quantity *
                                                        orderItem.price,
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="destructive"
                                                    size="icon"
                                                    type="button"
                                                    onClick={() => {
                                                        handleChangeQuantity(
                                                            orderItem.quantity,
                                                            orderItem.id,
                                                        );
                                                    }}
                                                    className="group bg-red-300 !p-1"
                                                >
                                                    <TbTrash className="h-5 w-5 text-red-800 group-hover:text-white" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                        <div className="flex justify-end">
                            <span className="font-semibold">
                                Total Return: {formatCurrencyInBDT(0)}
                            </span>
                        </div>
                        <div className="flex justify-end gap-4 pt-4">
                            <Button
                                onClick={() => {
                                    dispatch(closeModal());
                                }}
                                type="button"
                                variant={'secondary'}
                            >
                                Cancel
                            </Button>

                            <Dialog>
                                <DialogTrigger asChild>
                                    <Button>Return</Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-[425px]">
                                    <DialogHeader>
                                        <DialogTitle>
                                            Confirm Sales Return?
                                        </DialogTitle>
                                        <DialogDescription>
                                            Are you sure you want to proceed
                                            this action?
                                        </DialogDescription>
                                    </DialogHeader>
                                    <DialogFooter>
                                        <Button onClick={onSubmit}>
                                            Confirm
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </div>
                    </form>
                </Form>
            </div>
        );
    }

    return <div>Loading...</div>;
};
